import React, { useState, forwardRef } from 'react'
import { useMessaging } from './MessagingProvider'
import { TrashIcon } from '@heroicons/react/24/outline'
import { useMutation } from '@tanstack/react-query'
import { apiDelete } from '~/util/api'
import { Button } from '~/lib/ui/button'
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogFooter,
} from '~/lib/ui/alert-dialog'
import { useTranslation } from '~/hooks/useTranslation'
import { useToast } from '~/lib/ui/toast'

export const TrashButton = forwardRef(({ ...props }, ref) => {
  return (
    <Button
      variant="icon"
      ref={ref}
      className="rounded-lg p-2 hover:bg-intoo-red-destructive/15"
      {...props}
    >
      <TrashIcon className="h-5 text-intoo-red-destructive" />
    </Button>
  )
})
TrashButton.displayName = 'TrashButton'

export const DeleteThreadDialog = ({ threadId }) => {
  const translationScope = 'DeleteThreadDialog'
  const { location, currentThreadId } = useMessaging()
  const { t } = useTranslation(translationScope)
  const { toast } = useToast()

  const thisThreadId = threadId || currentThreadId
  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`${location.pathname}/${thisThreadId}.json`),
    mutationKey: ['deleteThread', thisThreadId],
    onSuccess: (res) => {
      toast({ description: t('success') })
      window.location.hash = res?.data?.latest_thread_id || 'inbox'
    },
    onError: (error) => {
      console.error('Error deleting thread:', thisThreadId, error)
      toast({ description: t('error'), variant: 'destructive' })
    },
  })

  return (
    <GenericAlertDialog
      onDeleteClick={() => deleteMutation.mutate()}
      translationScope={translationScope}
    >
      <TrashButton aria-label={t('title')} />
    </GenericAlertDialog>
  )
}

export const DeleteMessageDialog = ({ message }) => {
  const translationScope = 'DeleteMessageDialog'
  const { location, queryClient } = useMessaging()
  const { id: messageId, coach_message_thread_id: threadId } = message
  const { t } = useTranslation(translationScope)
  const { toast } = useToast()

  const url = `${location.pathname}/${threadId}/messages/${messageId}.json`
  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(url),
    mutationKey: ['deleteMessage', messageId],
    onSuccess: (res) => {
      const {
        thread_deleted: threadDeleted,
        latest_thread_id: latestThreadId,
      } = res?.data || {}
      if (threadDeleted && latestThreadId) {
        window.location.hash = latestThreadId
      }
      toast({ description: t('success') })
      queryClient.invalidateQueries(['messageThread', threadId])
    },
    onError: (error) => {
      console.error('Error deleting message:', messageId, error)
      toast({ description: t('error'), variant: 'destructive' })
    },
  })

  return (
    <GenericAlertDialog
      onDeleteClick={() => deleteMutation.mutate()}
      translationScope={translationScope}
    >
      <TrashButton aria-label={t('title')} />
    </GenericAlertDialog>
  )
}

const GenericAlertDialog = ({ onDeleteClick, translationScope, children }) => {
  const [open, setOpen] = useState(false)
  const { t } = useTranslation(translationScope)
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('title')}</AlertDialogTitle>
        </AlertDialogHeader>
        <div className="px-6">{t('message')}</div>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
          <AlertDialogAction onClick={onDeleteClick} variant="coachcta">
            {t('action')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
